import axios from 'axios';
import { getUrl } from './service-utils';
import authHeader from './auth-header';

export interface PublishTripRequest {
  tripId: string;
  excludeNotes: boolean;
  isDiscoverable: boolean;
}

export interface PublicTrip {
  id: string;
  // Note: originalTripId intentionally excluded - parent trip reference not exposed to UI
  publishDate: string;
  isDiscoverable: boolean;
  viewCount: number;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  shareCount: number;
  saveCount: number;

  // Flattened trip data (from summary endpoint)
  title?: string;
  description?: string;
  imageUrl?: string;
  destinationNames?: string[];
  destinationNamesDisplay?: string;
  destinationCount?: number;
  activityCount?: number;
  startDate?: string;
  endDate?: string;
  duration?: number | null;
  hasActivities?: boolean;
  hasDestinations?: boolean;
  engagementScore?: number;
  trendingScore?: number;
  isPopular?: boolean;
  isTrending?: boolean;

  // Legacy nested structure (for backward compatibility with old endpoints)
  tripSnapshot?: any;
}

export interface PublicTripsListResponse {
  trips: PublicTrip[];
  pagination?: {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
  };
}

class PublicTripService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  /**
   * Get all discoverable public trips with pagination
   */
  public async getDiscoverableTrips(
    page: number = 1,
    limit: number = 20
  ): Promise<PublicTripsListResponse> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips?page=${page}&limit=${limit}`
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching discoverable trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get trending public trips
   */
  public async getTrendingTrips(limit: number = 20): Promise<PublicTrip[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/trending?limit=${limit}`
      );
      return response.data.data.trips;
    } catch (error: any) {
      console.error(
        'Error fetching trending trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get my published trips (requires authentication)
   */
  public async getMyPublishedTrips(
    page: number = 1,
    limit: number = 20
  ): Promise<PublicTripsListResponse> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/my-trips?page=${page}&limit=${limit}`,
        {
          headers: authHeader()
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching my published trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Search public trips
   */
  public async searchPublicTrips(
    query: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PublicTripsListResponse> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/search?q=${encodeURIComponent(
          query
        )}&page=${page}&limit=${limit}`
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error searching public trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get public trips summary with pagination
   */
  public async getPublicTripsSummary(
    page: number = 1,
    limit: number = 20
  ): Promise<PublicTripsListResponse> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/summary?page=${page}&limit=${limit}`
      );
      return {
        trips: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error: any) {
      console.error(
        'Error fetching public trips summary:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Publish a trip to make it public
   */
  public async publishTrip(request: PublishTripRequest): Promise<PublicTrip> {
    try {
      const response = await axios.post(
        `${this.API_URL}public-trips`,
        request,
        {
          headers: authHeader()
        }
      );
      return response.data.data.publicTrip;
    } catch (error: any) {
      console.error(
        'Error publishing trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Republish an existing public trip (update snapshot)
   */
  public async republishTrip(
    tripId: string,
    request: Omit<PublishTripRequest, 'tripId'>
  ): Promise<PublicTrip> {
    try {
      const response = await axios.post(
        `${this.API_URL}public-trips/${tripId}/republish`,
        request,
        {
          headers: authHeader()
        }
      );
      return response.data.data.publicTrip;
    } catch (error: any) {
      console.error(
        'Error republishing trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Unpublish a trip (make it private again)
   */
  public async unpublishTrip(tripId: string): Promise<void> {
    try {
      await axios.delete(`${this.API_URL}public-trips/${tripId}`, {
        headers: authHeader()
      });
    } catch (error: any) {
      console.error(
        'Error unpublishing trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Check if a trip is already published
   */
  public async checkIfPublished(tripId: string): Promise<boolean> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/check/${tripId}`,
        {
          headers: authHeader()
        }
      );
      return response.data.data.isPublished;
    } catch (error: any) {
      console.error(
        'Error checking trip publication status:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get public trip by original trip ID
   */
  public async getPublicTripByTripId(tripId: string): Promise<PublicTrip> {
    try {
      const response = await axios.get(
        `${this.API_URL}public-trips/by-trip/${tripId}`,
        {
          headers: authHeader()
        }
      );
      return response.data.data.publicTrip;
    } catch (error: any) {
      console.error(
        'Error fetching public trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Change discoverability of a public trip
   */
  public async changeDiscoverability(
    publicTripId: string,
    isDiscoverable: boolean
  ): Promise<void> {
    try {
      await axios.patch(
        `${this.API_URL}public-trips/${publicTripId}/discoverability`,
        { isDiscoverable },
        {
          headers: authHeader()
        }
      );
    } catch (error: any) {
      console.error(
        'Error changing discoverability:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Like a public trip
   */
  public async likePublicTrip(publicTripId: string): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}public-trips/${publicTripId}/like`,
        {},
        {
          headers: authHeader()
        }
      );
    } catch (error: any) {
      console.error(
        'Error liking public trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Unlike a public trip
   */
  public async unlikePublicTrip(publicTripId: string): Promise<void> {
    try {
      await axios.delete(`${this.API_URL}public-trips/${publicTripId}/like`, {
        headers: authHeader()
      });
    } catch (error: any) {
      console.error(
        'Error unliking public trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const publicTripServiceInstance = new PublicTripService();
export default publicTripServiceInstance;
